import createClient from 'openapi-fetch';
import type { paths } from './schema.d.ts';

export const API_BASE = '/api/v1';

/** Error shape returned by the control plane's error handler (apps/api/src/app.ts). */
export interface ApiErrorBody {
  statusCode: number;
  error: string;
  message: string;
  code?: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
  static from(status: number, body: unknown): ApiError {
    const b = (body ?? {}) as Partial<ApiErrorBody>;
    return new ApiError(status, b.code ?? b.error ?? 'error', b.message ?? `request failed (${status})`);
  }
}

export const isApiError = (e: unknown, code?: string): e is ApiError =>
  e instanceof ApiError && (code === undefined || e.code === code);

/**
 * Token source + refresh hook, installed by auth/session.ts. Kept behind an interface so this module
 * (and every query) has no dependency on React or on the session store's shape.
 */
export interface TokenProvider {
  getToken(): string | null;
  /** Attempt a silent refresh; resolves true when a new access token is available. */
  refresh(): Promise<boolean>;
}
let tokens: TokenProvider = { getToken: () => null, refresh: async () => false };
export const setTokenProvider = (p: TokenProvider) => {
  tokens = p;
};

/** Absolute so it also works outside a browser (tests); same-origin in production behind Caddy. */
const origin = typeof location !== 'undefined' ? location.origin : 'http://localhost';
export const api = createClient<paths>({
  baseUrl: `${origin}${API_BASE}`,
  credentials: 'same-origin',
  // Resolve `fetch` per call rather than at import time so tests (and future instrumentation) can replace it.
  fetch: (req) => globalThis.fetch(req),
});
api.use({
  onRequest({ request }) {
    const t = tokens.getToken();
    if (t && !request.headers.has('authorization')) request.headers.set('authorization', `Bearer ${t}`);
    return request;
  },
});

interface Result<T> {
  data?: T;
  error?: unknown;
  response: Response;
}

/**
 * Unwraps an openapi-fetch result into data-or-throw. On a 401 that is about the access token — the
 * plain `unauthorized` the API answers a missing, expired or ended one with — it refreshes the session
 * once and retries, so an expired 15-minute access token is invisible to the user. Any other 401 is an
 * answer about what was sent (a wrong password, a missing second factor) and is never retried: a retry
 * would spend a second guess of the sign-in's ban budget (context BUG-0277). That rule, not the path,
 * is what keeps `/auth/login` and `/auth/refresh` out of it, and lets the account's own `/auth/*`
 * routes recover after a laptop sleeps.
 */
export async function call<T>(run: () => Promise<Result<T>>): Promise<T> {
  let r = await run();
  if (r.response.status === 401 && errorCode(r.error) === 'unauthorized' && (await tokens.refresh()))
    r = await run();
  if (r.error !== undefined || !r.response.ok) throw ApiError.from(r.response.status, r.error);
  return r.data as T;
}

const errorCode = (body: unknown): string | undefined =>
  body && typeof body === 'object' && 'code' in body && typeof body.code === 'string' ? body.code : undefined;

/**
 * The largest attachment the API takes, 25 MiB, so the composer can refuse one before sending a byte
 * (context STORY-0282). A copy of `MAX_UPLOAD_BYTES` in `packages/core`, which the API enforces and the
 * front end does not import; the server's own refusal still reaches the composer if the two ever differ.
 */
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

/**
 * A multipart `POST` that reports how much it has sent, which `fetch` cannot: the composer's per-file
 * progress bars (context STORY-0282). It carries the session the way `call` does, and refreshes it once
 * on the same plain `unauthorized`. `signal` cancels it, and rejects with an `AbortError`.
 */
export async function uploadWithProgress<T>(
  path: string,
  body: FormData,
  onProgress: (sent: number, total: number) => void,
  signal?: AbortSignal,
): Promise<T> {
  const once = () =>
    new Promise<{ status: number; body: unknown }>((resolve, reject) => {
      if (signal?.aborted) return reject(new DOMException('aborted', 'AbortError'));
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${origin}${API_BASE}${path}`);
      xhr.withCredentials = true;
      const t = tokens.getToken();
      if (t) xhr.setRequestHeader('authorization', `Bearer ${t}`);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(e.loaded, e.total);
      };
      xhr.onload = () => {
        let parsed: unknown;
        try {
          parsed = JSON.parse(xhr.responseText);
        } catch {
          parsed = undefined;
        }
        resolve({ status: xhr.status, body: parsed });
      };
      xhr.onerror = () => reject(new ApiError(0, 'network', 'the upload could not reach the server'));
      xhr.onabort = () => reject(new DOMException('aborted', 'AbortError'));
      signal?.addEventListener('abort', () => xhr.abort(), { once: true });
      xhr.send(body);
    });
  let r = await once();
  if (r.status === 401 && errorCode(r.body) === 'unauthorized' && (await tokens.refresh())) r = await once();
  if (r.status < 200 || r.status >= 300) throw ApiError.from(r.status, r.body);
  return r.body as T;
}
